// Data of the «Что нового» screen and its pure helpers: notes newest first, places of a note, «Где это?» search.
// No host specifics: a host (a widget, an app) loads the data and hands it to <WhatsNew>.
const normalize = (text) => text.toLowerCase().replace(/ё/g, "е");
const tokens = (text) => normalize(text).split(/[^\p{L}\d]+/u).filter((word) => word.length >= 3);
// Russian endings: «прогрузка» / «прогрузки», «сообщений» / «сообщения» meet on a common stem.
const stem = (word) => (word.length <= 4 ? word : word.slice(0, Math.max(4, Math.ceil(word.length * 0.7))));
const KIND_ORDER = { feature: 0, place: 1, request: 2 };
/** "Где это?": best matches first — more of the asked words, features before single places. */
export function searchWhere(index, query, limit = 6) {
    const asked = [...new Set(tokens(query).map(stem))];
    if (!asked.length)
        return [];
    const scored = index
        .map((entry) => {
        const own = tokens(`${entry.title} ${entry.words ?? ""} ${entry.places.map((place) => place.label).join(" ")}`);
        return { entry, score: asked.filter((part) => own.some((word) => word.startsWith(part))).length };
    })
        .filter((item) => item.score > 0);
    const best = Math.max(0, ...scored.map((item) => item.score));
    const order = (entry) => KIND_ORDER[entry.kind ?? ""] ?? 3;
    return scored
        .filter((item) => item.score >= Math.max(1, best - (asked.length > 2 ? 1 : 0)))
        .sort((a, b) => b.score - a.score || order(a.entry) - order(b.entry) || a.entry.title.localeCompare(b.entry.title, "ru"))
        .slice(0, limit)
        .map((item) => item.entry);
}
const LINK = /\[([^\]\n]+)\]\(([^)\s]+)\)/g;
/** Words after a link on its line («- [Вид](/?settings=view) — тема» → «тема»). */
const AFTER = /^\s*[—–-]\s*/;
const linksIn = (text) => text.split("\n").flatMap((line) => [...line.matchAll(LINK)].map((match) => {
    const rest = line.slice((match.index ?? 0) + match[0].length);
    const hint = AFTER.test(rest) && !/\[[^\]\n]+\]\(/.test(rest) ? rest.replace(AFTER, "").trim() : "";
    return { label: match[1], href: match[2], ...(hint ? { hint } : {}) };
}));
const sectionOf = (body, title) => new RegExp(`(?:^|\\n)## ${title}\\n([\\s\\S]*?)(?=\\n## |$)`).exec(body)?.[1] ?? null;
/** Where the note's change is: its own `links`, else «## Где смотреть» of the body, else every body link. */
export function placesOf(note) {
    if (Array.isArray(note.links) && note.links.length)
        return note.links.filter((link) => link && typeof link.label === "string");
    const where = sectionOf(note.body ?? "", "Где смотреть");
    const own = where !== null ? linksIn(where) : [];
    return own.length ? own : linksIn(note.body ?? "");
}
const plain = (text) => text.replace(LINK, "$1").replace(/^- /gm, "").replace(/\s+/g, " ").trim();
/** How to use the change, short: the note's own `how`, else «## Как пользоваться», else the first paragraph of the body. */
export function noteHow(note, max = 220) {
    const own = typeof note.how === "string" && note.how.trim() ? note.how : null;
    const body = note.body ?? "";
    const text = plain(own ?? sectionOf(body, "Как пользоваться") ?? body.split(/\n\s*\n|\n## /)[0] ?? "");
    if (text.length <= max)
        return text;
    const cut = text.slice(0, max);
    const end = Math.max(cut.lastIndexOf(". "), cut.lastIndexOf("; "));
    return end > max / 2 ? cut.slice(0, end + 1) : `${cut.replace(/\s+\S*$/, "")}…`;
}
/** The address a click on the note opens: the first place that has one. */
export const placeHref = (note) => placesOf(note).find((link) => !!link.href)?.href ?? null;
/** Newest first: by date and time. */
export const newestFirst = (notes) => [...notes].sort((a, b) => `${b.date} ${b.time ?? ""}`.localeCompare(`${a.date} ${a.time ?? ""}`));
/** The guide of a note's place: the place's own element and words, the note's title and how to use it. */
export function noteGuide(note, link) {
    if (!link.href)
        return null;
    const places = placesOf(note);
    const known = places.find((item) => item.href === link.href && item.label === link.label) ?? places.find((item) => item.href === link.href);
    return {
        href: link.href,
        place: link.place ?? known?.place ?? null,
        label: link.label,
        title: note.title,
        hint: link.hint ?? known?.hint ?? null,
        how: noteHow(note),
    };
}
const trimPath = (path) => path.replace(/\/+$/, "") || "/";
/** The page is the screen of `href`: the same path (with or without the host's base path in front) and its query is there. */
export function onScreen(href, at, base = "") {
    let want;
    try {
        want = new URL(href, at.origin);
    }
    catch {
        return false;
    }
    const path = trimPath(want.pathname);
    const here = trimPath(at.pathname);
    const withBase = trimPath(`${base}${path === "/" ? "" : path}`);
    if (here !== path && here !== withBase)
        return false;
    const params = new URLSearchParams(at.search);
    for (const [key, value] of want.searchParams)
        if (params.get(key) !== value)
            return false;
    return true;
}
/** The first visible element of `selector` that `skip` does not reject (a host's own overlay); a bad selector — none. */
export function findPlace(selector, skip) {
    if (!selector || typeof document === "undefined")
        return null;
    let found = [];
    try {
        found = [...document.querySelectorAll(selector)];
    }
    catch {
        return null;
    }
    return found.find((el) => el instanceof HTMLElement && !skip?.(el) && el.getClientRects().length > 0) ?? null;
}
